import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import Icon from './ui/Icon';

const ToastContext = createContext(null);

const ICONS = {
  success: 'check_circle',
  error: 'cancel',
  warning: 'warning',
  info: 'info'
};

const COLORS = {
  success: { bg: '#2fb65c', bar: '#1d8c3a', text: 'white' },
  error:   { bg: '#e74c3c', bar: '#c0392b', text: 'white' },
  warning: { bg: '#f39c12', bar: '#d68910', text: 'white' },
  info:    { bg: '#2980b9', bar: '#1a6091', text: 'white' }
};

const DURATION = 4000;

const ToastItem = ({ id, message, type = 'info', onRemove }) => {
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const color = COLORS[type] || COLORS.info;

  const startTimer = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const remaining = DURATION - elapsed;
    if (remaining <= 0) { handleRemove(); return; }

    timerRef.current = setTimeout(handleRemove, remaining);
    intervalRef.current = setInterval(() => {
      const el = Date.now() - startTimeRef.current;
      setProgress(Math.max(0, 100 - (el / DURATION) * 100));
    }, 30);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pauseTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(intervalRef.current);
  }, []);

  const handleRemove = useCallback(() => {
    setExiting(true);
    setTimeout(() => onRemove(id), 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, onRemove]);

  useEffect(() => {
    startTimer();
    return () => { clearTimeout(timerRef.current); clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      onMouseEnter={pauseTimer}
      onMouseLeave={() => { startTimeRef.current = Date.now() - (DURATION * (1 - progress / 100)); startTimer(); }}
      style={{
        ...toastStyles.item,
        background: color.bg,
        color: color.text,
        transform: exiting ? 'translateX(120%)' : 'translateX(0)',
        opacity: exiting ? 0 : 1,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        marginBottom: '10px'
      }}
    >
      <div style={toastStyles.icon}><Icon name={ICONS[type]} size={20} color={color.text} /></div>
      <div style={toastStyles.message}>{message}</div>
      <button
        onClick={handleRemove}
        style={{ ...toastStyles.closeBtn, color: color.text }}
        aria-label="Dismiss"
      >
        <Icon name="close" size={16} color={color.text} />
      </button>
      <div style={toastStyles.progressTrack}>
        <div style={{
          ...toastStyles.progressBar,
          width: `${progress}%`,
          background: color.bar
        }} />
      </div>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={toastStyles.container}>
        {toasts.map(t => (
          <ToastItem key={t.id} {...t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx.showToast;
};

const toastStyles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    pointerEvents: 'none'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: '280px',
    maxWidth: '420px',
    padding: '14px 16px 18px',
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    position: 'relative',
    overflow: 'hidden',
    pointerEvents: 'auto',
    cursor: 'default'
  },
  icon: {
    fontSize: '18px',
    fontWeight: 'bold',
    flexShrink: 0,
    width: '22px',
    textAlign: 'center'
  },
  message: {
    flex: 1,
    fontSize: '14px',
    lineHeight: '1.4',
    fontWeight: '500'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    opacity: 0.8,
    padding: '0 4px',
    flexShrink: 0,
    lineHeight: 1
  },
  progressTrack: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'rgba(0,0,0,0.15)'
  },
  progressBar: {
    height: '100%',
    transition: 'width 0.05s linear',
    borderRadius: '0 0 0 0'
  }
};
